import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cog, Euro, Shield, Wrench, Plus, Minus } from 'lucide-react';
import { useForm } from '../../hooks/useForm';
import { useToast } from '../Toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import TextArea from '../ui/Textarea';
import servicosService from '../../services/servicos';
const ServicoForm = ({ servico, onSuccess, onCancel }) => {
    const { success, error } = useToast();
    // Estados locais
    const [equipamentos, setEquipamentos] = useState(servico?.equipamentosSuportados || ['']);
    // Query para obter categorias
    const { data: categorias } = useQuery({
        queryKey: ['servicos-categorias'],
        queryFn: () => servicosService.getCategorias()
    });
    // Form
    const { values, errors, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = useForm({
        initialValues: {
            nome: servico?.nome || '',
            descricao: servico?.descricao || '',
            categoria: servico?.categoria || 'reparacao',
            preco: servico?.preco || 0,
            tempoEstimado: servico?.tempoEstimado || '3-5 dias úteis',
            garantia: servico?.garantia || '3 meses',
            observacoes: servico?.observacoes || '',
            ativo: servico?.ativo ?? true
        },
        validationRules: {
            nome: {
                required: 'Nome é obrigatório',
                minLength: { value: 3, message: 'Nome deve ter pelo menos 3 caracteres' }
            },
            categoria: {
                required: 'Categoria é obrigatória'
            },
            preco: {
                required: 'Preço é obrigatório',
                min: { value: 0, message: 'Preço deve ser maior ou igual a zero' }
            },
            tempoEstimado: {
                required: 'Tempo estimado é obrigatório'
            },
            garantia: {
                required: 'Garantia é obrigatória'
            }
        },
        onSubmit: async (formData) => {
            try {
                const servicoData = {
                    ...formData,
                    equipamentosSuportados: equipamentos.filter(e => e.trim() !== '')
                };
                if (servico) {
                    await servicosService.update(servico.id, servicoData);
                    success('Serviço atualizado com sucesso!');
                }
                else {
                    await servicosService.create(servicoData);
                    success('Serviço criado com sucesso!');
                }
                onSuccess();
            }
            catch (err) {
                error(err.message || 'Erro ao salvar serviço');
            }
        }
    });
    // Handlers para equipamentos suportados
    const adicionarEquipamento = () => {
        setEquipamentos([...equipamentos, '']);
    };
    const removerEquipamento = (index) => {
        if (equipamentos.length > 1) {
            setEquipamentos(equipamentos.filter((_, i) => i !== index));
        }
    };
    const atualizarEquipamento = (index, valor) => {
        const novosEquipamentos = [...equipamentos];
        novosEquipamentos[index] = valor;
        setEquipamentos(novosEquipamentos);
    };
    // Opções de categoria
    const categoriasOptions = [
        { value: 'ecra', label: 'Ecrã' },
        { value: 'bateria', label: 'Bateria' },
        { value: 'upgrade', label: 'Upgrade' },
        { value: 'carregamento', label: 'Carregamento' },
        { value: 'reparacao', label: 'Reparação' },
        { value: 'manutencao', label: 'Manutenção' }
    ];
    // Opções de tempo estimado
    const temposOptions = [
        { value: '1 hora', label: '1 hora' },
        { value: '2-3 horas', label: '2-3 horas' },
        { value: '1 dia útil', label: '1 dia útil' },
        { value: '2-3 dias úteis', label: '2-3 dias úteis' },
        { value: '3-5 dias úteis', label: '3-5 dias úteis' },
        { value: '1 semana', label: '1 semana' },
        { value: '2 semanas', label: '2 semanas' }
    ];
    // Opções de garantia
    const garantiaOptions = [
        { value: '1 mês', label: '1 mês' },
        { value: '3 meses', label: '3 meses' },
        { value: '6 meses', label: '6 meses' },
        { value: '1 ano', label: '1 ano' }
    ];
    return (_jsxs("form", { onSubmit: handleSubmit, className: "space-y-6", children: [_jsxs("div", { className: "flex items-center space-x-3 mb-6", children: [_jsx("div", { className: "p-2 bg-blue-100 rounded-lg", children: _jsx(Cog, { className: "w-6 h-6 text-blue-600" }) }), _jsxs("div", { children: [_jsx("h3", { className: "text-lg font-semibold text-gray-900", children: servico ? 'Editar Serviço' : 'Novo Serviço' }), _jsx("p", { className: "text-sm text-gray-500", children: servico ? 'Atualize as informações do serviço' : 'Preencha os dados do novo serviço' })] })] }), _jsxs("div", { className: "bg-gray-50 p-4 rounded-lg space-y-4", children: [_jsxs("div", { className: "flex items-center space-x-2 text-gray-700", children: [_jsx(Wrench, { className: "w-4 h-4" }), _jsx("span", { className: "font-medium", children: "Informa\u00E7\u00F5es B\u00E1sicas" })] }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-2 gap-4", children: [_jsx(Input, { label: "Nome do Servi\u00E7o", placeholder: "Ex: Troca de Ecr\u00E3 iPhone 14", ...getFieldProps('nome'), error: errors.nome, required: true }), _jsx(Select, { label: "Categoria", options: categoriasOptions, ...getFieldProps('categoria'), error: errors.categoria, required: true })] }), _jsx(TextArea, { label: "Descri\u00E7\u00E3o", placeholder: "Descreva detalhadamente o servi\u00E7o...", rows: 3, ...getFieldProps('descricao'), error: errors.descricao })] }), _jsxs("div", { className: "bg-gray-50 p-4 rounded-lg space-y-4", children: [_jsxs("div", { className: "flex items-center space-x-2 text-gray-700", children: [_jsx(Euro, { className: "w-4 h-4" }), _jsx("span", { className: "font-medium", children: "Pre\u00E7o e Tempos" })] }), _jsxs("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-4", children: [_jsx(Input, { label: "Pre\u00E7o (\u20AC)", type: "number", step: "0.01", min: "0", placeholder: "0.00", ...getFieldProps('preco'), error: errors.preco, required: true }), _jsx(Select, { label: "Tempo Estimado", options: temposOptions, ...getFieldProps('tempoEstimado'), error: errors.tempoEstimado, required: true }), _jsx(Select, { label: "Per\u00EDodo de Garantia", options: garantiaOptions, ...getFieldProps('garantia'), error: errors.garantia, required: true })] })] }), _jsxs("div", { className: "bg-gray-50 p-4 rounded-lg space-y-4", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsxs("div", { className: "flex items-center space-x-2 text-gray-700", children: [_jsx(Shield, { className: "w-4 h-4" }), _jsx("span", { className: "font-medium", children: "Equipamentos Suportados" })] }), _jsxs(Button, { type: "button", variant: "outline", size: "sm", onClick: adicionarEquipamento, children: [_jsx(Plus, { className: "w-4 h-4 mr-1" }), "Adicionar"] })] }), _jsx("div", { className: "space-y-2", children: equipamentos.map((equipamento, index) => (_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Input, { placeholder: "Ex: iPhone 14, Samsung Galaxy S21...", value: equipamento, onChange: (e) => atualizarEquipamento(index, e.target.value), className: "flex-1" }), equipamentos.length > 1 && (_jsx(Button, { type: "button", variant: "outline", size: "sm", onClick: () => removerEquipamento(index), className: "text-red-600 hover:bg-red-50", children: _jsx(Minus, { className: "w-4 h-4" }) }))] }, index))) })] }), _jsx("div", { className: "bg-gray-50 p-4 rounded-lg space-y-4", children: _jsx(TextArea, { label: "Observa\u00E7\u00F5es", placeholder: "Notas adicionais sobre o servi\u00E7o...", rows: 3, ...getFieldProps('observacoes'), error: errors.observacoes }) }), _jsx("div", { className: "bg-gray-50 p-4 rounded-lg", children: _jsxs("label", { className: "flex items-center space-x-2", children: [_jsx("input", { type: "checkbox", checked: values.ativo, onChange: (e) => setFieldValue('ativo', e.target.checked), className: "rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50" }), _jsx("span", { className: "text-sm text-gray-700", children: "Servi\u00E7o ativo" })] }) }), _jsxs("div", { className: "flex justify-end space-x-3 pt-6 border-t", children: [_jsx(Button, { type: "button", variant: "outline", onClick: onCancel, disabled: isSubmitting, children: "Cancelar" }), _jsxs(Button, { type: "submit", isLoading: isSubmitting, children: [servico ? 'Atualizar' : 'Criar', " Servi\u00E7o"] })] })] }));
};
export default ServicoForm;
